import { useCallback, useEffect, useRef, useState } from 'react'
import { benenneUm, hole, merke, raeumeAuf, vergiss, neuKennung } from '../net/entwurfsspeicher.js'
import { ApiError } from '../net/errors.js'

/**
 * Automatisches Speichern für die Belegerfassung.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was vorher verloren ging
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Alles. Der Formularzustand lebte ausschliesslich im Arbeitsspeicher der
 * Seite – kein `beforeunload`, kein lokaler Entwurf, kein Zeitgeber. Wer eine
 * halbe Stunde Positionen erfasste und das Telefon ausgehen liess, fing von
 * vorne an. Gespeichert wurde nur auf Knopfdruck, und dieser Knopf verlangte
 * zusätzlich einen Kunden und mindestens eine Position.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Wie es jetzt läuft
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Zwei Wege, und der Server hat Vorrang:
 *
 *   **Sofort, örtlich.** Jede Änderung schreibt eine Kopie in die
 *   Browser-Ablage. Das kostet nichts, braucht kein Netz und überlebt einen
 *   Absturz, einen leeren Akku und ein geschlossenes Fenster.
 *
 *   **Kurz darauf, auf den Server.** Nach einer Ruhepause von anderthalb
 *   Sekunden geht der Stand als EINE Anfrage hinaus. Dort ist er von jedem
 *   Gerät erreichbar, und dort gilt die Mandantentrennung der Datenbank.
 *
 * Die Ruhepause ist kein Sparprogramm, sondern Rücksicht: Wer tippt, soll
 * nicht bei jedem Buchstaben eine Anfrage auslösen. Anderthalb Sekunden sind
 * länger als eine Tippause und kürzer als die Zeit, in der jemand das Fenster
 * schliesst.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Keine falsche Erfolgsmeldung
 * ════════════════════════════════════════════════════════════════════════════
 *
 * «Gespeichert» steht erst da, wenn der Server geantwortet hat. Schlägt der
 * Versuch fehl, steht «Nicht gespeichert» – und die örtliche Kopie bleibt
 * liegen, damit beim nächsten Aufruf die Wiederherstellung angeboten wird.
 * Ein Häkchen, das nur bedeutet «wir haben es versucht», ist schlimmer als
 * gar keine Anzeige.
 */

/**
 * `konflikt` ist der vierte Zustand, und er ist der einzige, der eine
 * Entscheidung verlangt: Der Beleg wurde woanders geändert. Die Eingaben
 * liegen vollständig in der Browser-Ablage; was damit geschieht, entscheidet
 * der Mensch – nicht die Sicherung, und erst recht nicht stillschweigend.
 */
export type Sicherungszustand =
  | 'ruht'
  | 'speichert'
  | 'gespeichert'
  | 'ungesichert'
  | 'konflikt'

export interface Sicherungsbefund<T> {
  /** Was angezeigt wird. */
  zustand: Sicherungszustand
  /** Ein gefundener örtlicher Stand, der sich wiederherstellen liesse. */
  gefunden: { stand: T; gespeichertAm: number } | null
  /** Den gefundenen Stand übernehmen – danach ist er nicht mehr im Angebot. */
  uebernehmen: () => void
  /** Den gefundenen Stand verwerfen. */
  verwerfen: () => void
  /** Sofort sichern, ohne auf die Ruhepause zu warten. */
  jetzt: () => void
  /**
   * Einen Konflikt für gelöst erklären – nachdem der Mensch entschieden hat.
   *
   * `alsGesichert` sagt, welcher der beiden Wege gegangen wurde: Der Stand der
   * Maske liegt jetzt auf dem Server (true), oder die Maske zeigt jetzt den
   * Serverstand (false). Beides beendet die Sperre, die ein gescheiterter
   * Versuch hinterlässt – sonst bliebe der Hinweis stehen, obwohl die Sache
   * entschieden ist, und die nächste Eingabe ginge nicht mehr hinaus.
   */
  entwarnen: (alsGesichert: boolean) => void
}

const RUHEPAUSE_MS = 1500

export function useEntwurfsSicherung<T>(opt: {
  /** Belegart – entscheidet über die vorläufige Kennung. */
  art: 'rechnung' | 'offerte'
  /** Der aktuelle Formularzustand. Muss sich bei jeder Änderung ändern. */
  stand: T
  /** Die Belegkennung, sobald der Server eine vergeben hat. */
  belegId: string | null
  /** Die Version des Serverstands, für die Konflikterkennung. */
  serverVersion: number | null
  /** Mandant und Person – für die Trennung in der Browser-Ablage. */
  wer: { tenantId: string; userId: string } | null
  /**
   * Erst wenn der Benutzer wirklich etwas geändert hat.
   *
   * Ohne diese Bedingung schriebe schon das blosse Öffnen der Maske eine
   * Kopie, und beim nächsten Aufruf stünde eine Wiederherstellung eines
   * leeren Formulars im Angebot.
   */
  aktiv: boolean
  /** Auf den Server sichern. Wirft bei Fehlschlag. Gibt die Belegkennung zurück. */
  aufServer: () => Promise<string | null>
  /** Einen wiederhergestellten Stand in die Maske setzen. */
  aufEinsetzen: (stand: T) => void
}): Sicherungsbefund<T> {
  const { art, stand, belegId, serverVersion, wer, aktiv, aufServer, aufEinsetzen } = opt

  const [zustand, setZustand] = useState<Sicherungszustand>('ruht')
  const [gefunden, setGefunden] = useState<{ stand: T; gespeichertAm: number } | null>(null)
  const geprueft = useRef(false)
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null)
  /*
    Der aktuelle Stand in einer Referenz.

    Der Zeitgeber feuert später; läse er `stand` aus der Closure, sicherte er
    den Stand von vor anderthalb Sekunden. Genau so verliert man die letzte
    Eingabe vor dem Schliessen des Fensters.
  */
  const jetzigerStand = useRef(stand)
  jetzigerStand.current = stand
  const laeuft = useRef(false)
  /* Während eines Laufs kam neue Eingabe – nach dem Lauf noch einmal sichern. */
  const nachholen = useRef(false)
  /*
    Was zuletzt erfolgreich beim Server lag – als Zeichenkette zum Vergleichen.

    Ohne diesen Vergleich dreht die Sicherung sich im Kreis: Das Speichern
    legt beim ersten Mal den Beleg an, dadurch ändern sich `belegId` und
    `serverVersion`, dadurch läuft der Effekt erneut, dadurch wird erneut
    gespeichert – und die Anzeige bleibt für immer auf «Speichert …». Beim
    Nachsehen im Browser genau so beobachtet.

    Verglichen wird der INHALT, nicht die Kennung. Nur wenn der Mensch etwas
    geändert hat, geht etwas hinaus.
  */
  const zuletztGesichert = useRef<string | null>(null)
  /*
    Was zuletzt VERGEBLICH versucht wurde.

    Ohne diesen Vermerk dreht sich die Sicherung bei einem dauerhaften
    Hindernis im Kreis: Der Versuch scheitert, `setZustand('ungesichert')`
    erzeugt einen neuen Durchlauf, der Durchlauf startet den Zeitgeber neu,
    der Versuch scheitert wieder – alle anderthalb Sekunden, endlos, und die
    Anzeige steht dabei meistens auf «Speichert …».

    Beobachtet im Browser an genau dem Fall, der diese Runde abschliesst:
    einem Beleg ohne Kunden. Der Server kann ihn nicht anlegen –
    `documents.customer_id` verlangt einen –, also scheitert jeder Versuch,
    und zwar beständig.

    Jetzt gilt: Derselbe Inhalt wird kein zweites Mal hinausgeschickt. Die
    örtliche Kopie wird trotzdem bei jeder Änderung geschrieben, und die
    Anzeige bleibt ehrlich auf «nicht gesichert» stehen. Ändert der Mensch
    etwas – wählt er etwa den Kunden –, ist der Abdruck ein anderer und der
    nächste Versuch läuft.
  */
  const zuletztVergeblich = useRef<string | null>(null)

  const kennung = belegId ?? neuKennung(art)
  /* Die Kennung als Referenz – der Nachholer läuft später und braucht die von dann. */
  const kennungJetzt = useRef(kennung)
  kennungJetzt.current = kennung

  /* ── Beim ersten Öffnen nachsehen, ob etwas liegen geblieben ist ──────── */
  useEffect(() => {
    if (!wer || geprueft.current) return
    geprueft.current = true
    void raeumeAuf()
    void hole(kennung, wer).then((e) => {
      if (!e) return
      setGefunden({ stand: e.stand as T, gespeichertAm: e.gespeichertAm })
    })
  }, [wer, kennung])

  /*
    ──────────────────────────────────────────────────────────────────────────
    EIN Sicherungslauf – und zwar nur einer, mit Nachholen
    ──────────────────────────────────────────────────────────────────────────

    Hier stand dieser Rumpf direkt im Zeitgeber, und darin ein
    `if (laeuft.current) return`. Das war ein stiller Verlust: Wer während
    eines laufenden Laufs weitertippte, dessen letzte Eingaben gingen nie
    hinaus – und die Anzeige stand dabei auf «Speichert …» und behauptete das
    Gegenteil. Gemessen mit schnellen aufeinanderfolgenden Eingaben.

    Jetzt ist der Lauf eine eigene Funktion, die der Zeitgeber aufruft und die
    sich am Ende selbst noch einmal aufruft, wenn in der Zwischenzeit etwas
    dazugekommen ist. Sie liest den Stand aus der Referenz, nie aus einer
    Closure – sonst sicherte das Nachholen den Stand von vorhin.
  */
  const sichereJetzt = useCallback(
    async function lauf(): Promise<void> {
      if (laeuft.current) {
        nachholen.current = true
        return
      }
      const abdruck = JSON.stringify(jetzigerStand.current)
      if (abdruck === zuletztGesichert.current) return
      if (abdruck === zuletztVergeblich.current) return
      laeuft.current = true
      setZustand('speichert')
      try {
        const neueId = await aufServer()
        zuletztGesichert.current = abdruck
        /*
          Hat der Server den Beleg gerade erst angelegt, zieht die örtliche
          Kopie auf die neue Kennung um. Bliebe sie unter `neu:rechnung`
          liegen, böte die Maske sie beim nächsten neuen Beleg als
          Wiederherstellung an – obwohl ihr Inhalt längst gespeichert ist.
          Das ist der Weg, auf dem doppelte Entwürfe entstehen.
        */
        if (neueId && kennungJetzt.current !== neueId) {
          await benenneUm(kennungJetzt.current, neueId)
        }
        await vergiss(neueId ?? kennungJetzt.current)
        /*
          Und die vorläufige Kennung gleich mit – auch wenn sie längst
          umbenannt sein müsste.

          Beim ersten Sichern eines neuen Belegs schreibt die örtliche Kopie
          unter `neu:rechnung`, bis der Server die Kennung vergibt; das
          Umbenennen zieht sie danach um. Wer schnell tippt, erzeugt in diesem
          Fenster mehrere Schreibvorgänge, und einer davon landet NACH dem
          Umbenennen wieder unter der vorläufigen Kennung. Gemessen am
          gebauten Stand: tippen, Status «Gespeichert», Seite neu laden, Maske
          öffnen – Band da, und in der Ablage steht `neu:rechnung` mit genau
          dem Inhalt, der auf dem Server liegt. Das Angebot führt dann zu
          einem zweiten Entwurf desselben Belegs.

          Der Beleg hat jetzt eine eigene Kennung; unter der vorläufigen darf
          nichts mehr liegen.
        */
        if (neueId) await vergiss(neuKennung(art))
        setZustand('gespeichert')
      } catch (err) {
        /* Die örtliche Kopie bleibt liegen – sie ist jetzt das Einzige. */
        zuletztVergeblich.current = abdruck
        /*
          Ein Versionskonflikt ist etwas anderes als ein Fehlschlag.

          «Nicht gesichert» stimmte zwar, sagte aber nicht, warum, und der Weg
          daraus war ein Neuladen, das die Eingaben wegwirft. Ein Konflikt
          heisst: Der Beleg wurde woanders geändert. Darüber entscheidet die
          Sicherung nicht selbst – sie hält an, benennt den Zustand und lässt
          die Maske die beiden Wege anbieten. Von selbst wird hier nichts
          überschrieben.
        */
        const konflikt =
          err instanceof ApiError &&
          (err.options.code === 'VERSION_CONFLICT' || err.options.status === 409)
        setZustand(konflikt ? 'konflikt' : 'ungesichert')
      } finally {
        laeuft.current = false
        /*
          Steht noch etwas aus, läuft es sofort nach – der Stand ist schon
          anderthalb Sekunden alt, eine zweite Ruhepause wäre nur Wartezeit.
        */
        if (nachholen.current) {
          nachholen.current = false
          void lauf()
        }
      }
    },
    [aufServer]
  )

  /* ── Örtlich sofort, auf den Server nach der Ruhepause ────────────────── */
  useEffect(() => {
    if (!aktiv || !wer) return
    const abdruck = JSON.stringify(stand)
    if (abdruck === zuletztGesichert.current) return
    /*
      Die örtliche Kopie IMMER zuerst, auch wenn der Server gerade nicht
      annimmt. Sie ist in diesem Fall das Einzige, was es gibt.
    */
    void merke(kennung, wer, art, stand, serverVersion)
    /*
      Derselbe Inhalt, der schon einmal vergeblich hinausging, wird nicht
      erneut versucht. Die Anzeige bleibt auf «nicht gesichert» – richtig, und
      der Server bleibt in Ruhe.
    */
    if (abdruck === zuletztVergeblich.current) return
    setZustand('speichert')

    if (uhr.current) clearTimeout(uhr.current)
    uhr.current = setTimeout(() => {
      void sichereJetzt()
    }, RUHEPAUSE_MS)

    return () => {
      if (uhr.current) clearTimeout(uhr.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand, aktiv, wer, kennung, art, serverVersion, sichereJetzt])

  /* ── Wenn die Seite verschwindet: wenigstens örtlich ──────────────────── */
  useEffect(() => {
    if (!aktiv || !wer) return
    const sichern = (): void => {
      /*
        Nur, wenn es etwas zu sichern gibt.

        Hier wurde bedingungslos geschrieben – auch wenn der Stand Sekunden
        vorher bestätigt beim Server lag. Beim nächsten Öffnen stand dann das
        Band «Von dieser Erfassung liegt ein ungesicherter Stand vor … und ging
        nicht an den Server», obwohl er genau dort lag. Gemessen am gebauten
        Stand: tippen, Status «Gespeichert», Seite neu laden, Maske öffnen –
        Band da.

        Das ist die Umkehrung der falschen Erfolgsmeldung und genauso
        schädlich: Wer das Angebot annimmt und erneut speichert, legt einen
        zweiten Entwurf desselben Belegs an. Der Vergleich gegen den letzten
        bestätigten Abdruck kostet nichts und macht den Satz wahr.
      */
      const abdruck = JSON.stringify(jetzigerStand.current)
      if (abdruck === zuletztGesichert.current) return
      void merke(kennung, wer, art, jetzigerStand.current, serverVersion)
    }
    /*
      Zwei Ereignisse, weil keines allein reicht: `pagehide` kommt beim
      Schliessen und beim Zurückblättern, `visibilitychange` beim Wegschalten
      auf dem Telefon – und genau dort wird die Seite danach oft ohne weitere
      Ereignisse entsorgt.
    */
    const beiSicht = (): void => {
      if (document.visibilityState === 'hidden') sichern()
    }
    window.addEventListener('pagehide', sichern)
    document.addEventListener('visibilitychange', beiSicht)
    return () => {
      window.removeEventListener('pagehide', sichern)
      document.removeEventListener('visibilitychange', beiSicht)
    }
  }, [aktiv, wer, kennung, art, serverVersion])

  const uebernehmen = useCallback(() => {
    if (!gefunden) return
    /*
      Vor dem Einsetzen nachsehen, ob der Stand noch da ist.

      Der Fund wird beim Öffnen der Maske EINMAL gelesen und bleibt danach im
      Zustand liegen. Verschwindet die örtliche Kopie in der Zwischenzeit –
      weil ein Lauf sie nach erfolgreichem Sichern aufräumt oder weil das
      Aufräumen sie als abgelaufen entfernt –, zeigt das Band weiter ein
      Angebot, hinter dem nichts steht. Gemessen am gebauten Stand: Band da,
      Ablage leer. Wer dann «Wiederherstellen» drückt, setzt einen Stand in
      ein leeres Formular, der längst auf dem Server liegt – und speichert ihn
      als ZWEITEN Entwurf.

      Deshalb wird hier noch einmal gelesen. Ist nichts mehr da, verschwindet
      das Angebot still: Es gibt nichts wiederherzustellen, weil nichts fehlt.
    */
    void (async () => {
      if (!wer) return
      const noch = await hole(kennungJetzt.current, wer)
      if (!noch) {
        setGefunden(null)
        return
      }
      aufEinsetzen(noch.stand as T)
      setGefunden(null)
    })()
  }, [gefunden, aufEinsetzen, wer])

  const verwerfen = useCallback(() => {
    setGefunden(null)
    void vergiss(kennung)
  }, [kennung])

  const entwarnen = useCallback((alsGesichert: boolean) => {
    /*
      Der Vermerk «das ging schon einmal vergeblich hinaus» muss weg.

      Er ist die Bremse gegen Endlosversuche, und nach einer Entscheidung ist
      er genau die falsche: Ohne das Zurücksetzen würde dieselbe Eingabe nie
      mehr versucht, weil ihr Abdruck als gescheitert gilt.
    */
    zuletztVergeblich.current = null
    if (alsGesichert) {
      zuletztGesichert.current = JSON.stringify(jetzigerStand.current)
      setZustand('gespeichert')
    } else {
      zuletztGesichert.current = null
      setZustand('ruht')
    }
  }, [])

  const jetzt = useCallback(() => {
    if (uhr.current) clearTimeout(uhr.current)
    if (!wer) return
    void merke(kennung, wer, art, jetzigerStand.current, serverVersion)
  }, [wer, kennung, art, serverVersion])

  return { zustand, gefunden, uebernehmen, verwerfen, jetzt, entwarnen }
}
